import { X, TrendingUp, MapPin, Globe2, Plane } from 'lucide-react';
import type { FriendStats, FlightStats } from '@/types';

interface ComparisonDashboardProps {
  userStats: FlightStats;
  friendStats: FriendStats;
  friendName: string;
  onClose: () => void;
}

export function ComparisonDashboard({
  userStats,
  friendStats,
  friendName,
  onClose,
}: ComparisonDashboardProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="glass rounded-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <TrendingUp className="w-6 h-6 text-primary-500" />
            <h2 className="text-2xl font-bold">Stats Comparison</h2>
          </div>
          <button onClick={onClose} className="btn-ghost p-2">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto flight-scroll p-6">
          <div className="grid grid-cols-2 gap-6">
            {/* Your Stats */}
            <div>
              <h3 className="text-xl font-semibold mb-4 text-primary-500">Your Stats</h3>
              
              <div className="space-y-4">
                <StatCard
                  icon={<Plane className="w-5 h-5" />}
                  label="Total Flights"
                  value={userStats.totalFlights}
                />
                <StatCard
                  icon={<MapPin className="w-5 h-5" />}
                  label="Unique Airports"
                  value={userStats.uniqueAirports}
                />
                <StatCard
                  icon={<Globe2 className="w-5 h-5" />}
                  label="Countries Visited"
                  value={userStats.uniqueCountries}
                />
              </div>
            </div>

            {/* Friend Stats */}
            <div>
              <h3 className="text-xl font-semibold mb-4 text-green-500">{friendName}'s Stats</h3>
              
              <div className="space-y-4">
                <StatCard
                  icon={<Plane className="w-5 h-5" />}
                  label="Total Flights"
                  value={friendStats.totalFlights}
                />
                <StatCard
                  icon={<MapPin className="w-5 h-5" />}
                  label="Unique Airports"
                  value={friendStats.uniqueAirports}
                />
                <StatCard
                  icon={<Globe2 className="w-5 h-5" />}
                  label="Countries Visited"
                  value={friendStats.uniqueCountries}
                />
              </div>

              {friendStats.mostVisitedAirports.length > 0 && (
                <div className="mt-6 glass-card p-4">
                  <h4 className="text-sm font-medium text-white/60 mb-2">Most Visited Airport</h4>
                  <p className="font-semibold">{friendStats.mostVisitedAirports[0].name}</p>
                  <p className="text-sm text-white/60">
                    {friendStats.mostVisitedAirports[0].city}, {friendStats.mostVisitedAirports[0].country}
                  </p>
                  <p className="text-xs text-white/40 mt-1">
                    {friendStats.mostVisitedAirports[0].visits} visit{friendStats.mostVisitedAirports[0].visits !== 1 ? 's' : ''}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Top Airports Comparison */}
          {friendStats.mostVisitedAirports.length > 0 && (
            <div className="mt-8">
              <h3 className="text-xl font-semibold mb-4">
                {friendName}'s Top Airports
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {friendStats.mostVisitedAirports.slice(0, 5).map((airport) => (
                  <div key={airport.iata} className="glass-card p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-semibold">{airport.name}</p>
                        <p className="text-sm text-white/60">
                          {airport.city}, {airport.country}
                        </p>
                        <p className="text-xs text-white/40 mt-1">
                          IATA: {airport.iata}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-primary-500">{airport.visits}</p>
                        <p className="text-xs text-white/40">visit{airport.visits !== 1 ? 's' : ''}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: number;
}

function StatCard({ icon, label, value }: StatCardProps) {
  return (
    <div className="glass-card p-4 flex items-center gap-4">
      <div className="text-primary-500">{icon}</div>
      <div>
        <p className="text-sm text-white/60">{label}</p>
        <p className="text-2xl font-bold">{value}</p>
      </div>
    </div>
  );
}
